import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface FavoriteButtonProps {
  propertyId: string;
  className?: string;
  size?: "sm" | "default" | "lg";
  variant?: "default" | "ghost" | "outline";
}

interface StoredAuthSession {
  userId: string | null;
  accessToken: string | null;
}

const getStoredAuthSession = (): StoredAuthSession => {
  try {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const raw = localStorage.getItem(`sb-${projectId}-auth-token`);
    if (!raw) return { userId: null, accessToken: null };

    const parsed = JSON.parse(raw);
    const session = parsed?.currentSession ?? parsed?.session ?? (Array.isArray(parsed) ? parsed[0] : null);

    return {
      userId: session?.user?.id ?? null,
      accessToken: session?.access_token ?? null,
    };
  } catch {
    return { userId: null, accessToken: null };
  }
};

const createAuthHeaders = (accessToken: string, includeJson = false): HeadersInit => ({
  apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  Authorization: `Bearer ${accessToken}`,
  ...(includeJson ? { "Content-Type": "application/json" } : {}),
});

export const FavoriteButton = ({
  propertyId,
  className,
  size = "sm",
  variant = "ghost",
}: FavoriteButtonProps) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authSession, setAuthSession] = useState<StoredAuthSession>({ userId: null, accessToken: null });

  useEffect(() => {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const authStorageKey = `sb-${projectId}-auth-token`;

    setAuthSession(getStoredAuthSession());

    const onStorageChange = (event: StorageEvent) => {
      if (!event.key || event.key === authStorageKey) {
        setAuthSession(getStoredAuthSession());
      }
    };

    window.addEventListener("storage", onStorageChange);
    return () => window.removeEventListener("storage", onStorageChange);
  }, []);

  useEffect(() => {
    if (authSession.userId && authSession.accessToken) {
      checkIfFavorite(authSession.userId, authSession.accessToken);
    } else {
      setIsFavorite(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSession.userId, authSession.accessToken, propertyId]);

  const checkIfFavorite = async (activeUserId: string, accessToken: string) => {
    try {
      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const url = `${baseUrl}/rest/v1/user_favorites?select=id&user_id=eq.${encodeURIComponent(activeUserId)}&property_id=eq.${encodeURIComponent(propertyId)}&limit=1`;
      const response = await fetch(url, {
        method: "GET",
        headers: createAuthHeaders(accessToken),
      });

      if (!response.ok) {
        if (response.status === 401) setIsFavorite(false);
        return;
      }

      const data = await response.json();
      setIsFavorite(Array.isArray(data) && data.length > 0);
    } catch (error) {
      console.error("Error checking favorite status:", error);
    }
  };

  const toggleFavorite = async () => {
    const { userId, accessToken } = authSession;

    if (!userId || !accessToken) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to save properties to favorites",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const filterQuery = `user_id=eq.${encodeURIComponent(userId)}&property_id=eq.${encodeURIComponent(propertyId)}`;

      if (isFavorite) {
        const response = await fetch(`${baseUrl}/rest/v1/user_favorites?${filterQuery}`, {
          method: "DELETE",
          headers: createAuthHeaders(accessToken),
        });

        if (!response.ok) throw new Error("Failed to remove favorite");

        setIsFavorite(false);
        toast({ title: "Removed from Favorites", description: "Property removed from your favorites" });
      } else {
        const response = await fetch(`${baseUrl}/rest/v1/user_favorites`, {
          method: "POST",
          headers: createAuthHeaders(accessToken, true),
          body: JSON.stringify({ user_id: userId, property_id: propertyId }),
        });

        if (!response.ok) throw new Error("Failed to add favorite");

        setIsFavorite(true);
        toast({ title: "Added to Favorites", description: "Property saved to your favorites" });
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast({
        title: "Error",
        description: "Failed to update favorites",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={toggleFavorite}
      variant={variant}
      size={size}
      disabled={isLoading}
      aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
      className={cn(
        "transition-all duration-200 hover:scale-110 relative group",
        isFavorite
          ? "text-red-600 hover:text-red-700 bg-white/95 hover:bg-white shadow-lg border border-red-100"
          : "text-red-400 hover:text-red-600 bg-white/80 hover:bg-white/95 backdrop-blur-sm border border-white/50",
        className
      )}
    >
      <Heart
        className={cn(
          "h-4 w-4 transition-all duration-200",
          isFavorite && "fill-red-600 drop-shadow-sm",
          isLoading && "animate-pulse"
        )}
      />
      {size !== "sm" && <span className="ml-2">{isFavorite ? "Favorited" : "Add to Favorites"}</span>}

      <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
        {isFavorite ? "Remove from favorites" : "Add to favorites"}
      </div>
    </Button>
  );
};
